import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  Input,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { EtJournalSigner, IDENTIFICATION_METHOD_OPTIONS } from '@et/typings';
import { finalize } from 'rxjs';
import { JournalSignerAddEditComponent } from '../journal-signer-add-edit/journal-signer-add-edit.component';
import { SignaturePadComponent } from '../signature-pad/signature-pad.component';
import { EjournalService } from '../../services/ejournal.service';
import { PresentationalAtomsModule } from '@et/presentational/atoms';

@Component({
  selector: 'et-journal-signer-card',
  imports: [DatePipe, MatDialogClose, PresentationalAtomsModule],
  templateUrl: './journal-signer-card.component.html',
  styleUrl: './journal-signer-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalSignerCardComponent {
  @Input({ required: true }) signer!: EtJournalSigner;
  @Input({ required: true }) isFinalized!: boolean;
  @Input() hideSignatures = false;

  @ViewChild('deleteSignerModal') deleteSignerModal!: TemplateRef<unknown>;
  @ViewChild('deleteSignatureModal')
  deleteSignatureModal!: TemplateRef<unknown>;

  private dialog = inject(MatDialog);
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);
  private ejournalService = inject(EjournalService);

  isDeletingSignature = signal<boolean>(false);
  isDeletingSigner = signal<boolean>(false);

  /**
   * Returns the identification method label for the signer.
   */
  get identificationMethod() {
    return IDENTIFICATION_METHOD_OPTIONS.find(
      (option) =>
        option.value.toLowerCase() ===
        this.signer.identificationMethod.toLowerCase(),
    )?.label;
  }

  /**
   * Opens the delete signer modal. If the dialog is closed and the user confirms the deletion,
   * it emits the deleteSigner event with the signer ID.
   */
  onDeleteSigner() {
    this.dialog.open(this.deleteSignerModal, {
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      id: 'delete-signer-modal',
    });
  }

  deleteSigner() {
    const signerId = this.signer.eJournalSignerId;
    this.isDeletingSigner.set(true);
    this.ejournalService
      .deleteSigner(signerId)
      .pipe(finalize(() => this.isDeletingSigner.set(false)))
      .subscribe(() => {
        this.dialog.getDialogById('delete-signer-modal')?.close();
      });
  }

  /**
   * Opens the edit signer modal.
   */
  onEditSigner() {
    this.dialog.open(JournalSignerAddEditComponent, {
      injector: this.injector,
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: {
        signer: this.signer,
      },
    });
  }

  /**
   * Opens the signature pad for this signer; refreshes change detection when the dialog closes.
   */
  onAddEditSignature() {
    this.dialog
      .open<SignaturePadComponent, EtJournalSigner>(SignaturePadComponent, {
        injector: this.injector,
        panelClass: ['et-dialog-radius', 'et-dialog-padding'],
        autoFocus: false,
        maxWidth: '900px',
        maxHeight: '90vh',
        data: this.signer,
      })
      .afterClosed()
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * Opens the delete-signature confirmation modal; on confirm, emits the signature id to the parent.
   */
  onDeleteSignature() {
    this.dialog.open(this.deleteSignatureModal, {
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      id: 'delete-signature-modal',
    });
  }

  /**
   * Delegates signature deletion to the add-ejournal service (API + local signer state).
   *
   * @param signerId - Signer whose signature list is updated locally.
   * @param signatureId - Signature id to remove on the server.
   */
  deleteSignature() {
    const signerId = this.signer.eJournalSignerId;
    const signatureId = this.signer.signatures[0].id;
    this.isDeletingSignature.set(true);
    this.ejournalService
      .deleteSignerSignature(signerId, signatureId)
      .pipe(finalize(() => this.isDeletingSignature.set(false)))
      .subscribe(() => {
        this.dialog.getDialogById('delete-signature-modal')?.close();
      });
  }
}
